import React, { useContext } from 'react';
import classes from './OpportunityPage.module.scss';
import propertyClasses from '../Resources/ResourceCard/ResourceCardPanels/ResourceCardPanel.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OpportunityProperties from './OpportunityProperties';
import { resourceHelpers, searchHelpers } from '@/utils/helpers';
import useMixpanel from '@/hooks/useMixpanel';
import useLog from '@/hooks/useLog';
import { AccountContext } from '@/context/account.context';
import t from '@/utils/Language/ResourceCard-translations';

// Eligibility-related properties render in their own badged sub-section, separate from
// the general property list.
const ELIGIBILITY_SECTION_PROPS = ['eligibility-requirements', 'citizenship-requirements', 'elig-available-everyone'];

// Mirrors Angular's Files.parseFilename: the stored filename is the URL's last path segment
// (before any query string). Used for tracking so the captured filename matches Angular and is
// never blank -- unlike display_name, which the ODRS Attachment model does not require.
const parseFilename = (url) => url?.match(/\/([^/]+)(\?|$)/)?.[1];

const DetailsSection = ({ opportunity }) => {
  const { track } = useMixpanel();
  const { log } = useLog();
  const account = useContext(AccountContext);
  const listedProperties = resourceHelpers.listedProperties(opportunity?.properties, searchHelpers);
  const signupUrlProperty = opportunity?.properties?.['action-signup-url'];
  const signupUrl = signupUrlProperty?.value ?? signupUrlProperty;
  const attachments = opportunity?.attachments || [];
  // The opportunity's own schedule only -- a location's schedule stays in the location section.
  // generateSchedule returns a table when there are hours, the notes string when every day is
  // closed, or null when there is nothing to show.
  const schedule = resourceHelpers.emptySchedule(opportunity?.schedule) ? null : resourceHelpers.generateSchedule(opportunity.schedule, false, true);

  const handleAttachmentClick = (attachment) => {
    const filename = parseFilename(attachment.url);
    track('Downloaded attachment', {
      resourceId: opportunity.id,
      resourceType: opportunity.resource_type,
      filename,
      fileType: attachment.file_type
    });

    // Matches Angular's downloadAttachment (controllers/resources/show.js): the 'Identified need'
    // mixpanel event and its paired activity-log entry fire only for a logged-in, non-pro member.
    if (account.isLoggedIn && !account.isPro) {
      const eventHash = resourceHelpers.mixpanelEventHash(opportunity);
      track('Identified need', { ...eventHash, sourceEvent: 'download attachment', filename });
      log({
        event: 'identified need',
        via: 'download attachment',
        message: filename,
        object_type: opportunity.resource_type,
        object_id: opportunity.id
      });
    }
  };

  if (!Object.keys(listedProperties).length && !signupUrl && !attachments.length && !schedule) {
    return null;
  }

  return (
    <div className={classes.eligibilitySection} id="opp-details">
      <div className={classes.eligibilitySectionPanel}>
        <div className={classes.eligibilitySectionHeader}>
          <FontAwesomeIcon icon={['far', 'circle-check']} className={classes.eligibilitySectionIcon} />
          <h2 className={classes.eligibilitySectionTitle}>{t('Details')}</h2>
        </div>
        <div className={classes.eligibilitySectionContent}>
          {schedule && (
            <div className={propertyClasses.propertyList}>
              <div className={propertyClasses.propertyRow}>
                <div className={propertyClasses.propertyLabel}>
                  <FontAwesomeIcon icon={['fas', 'calendar-days']} className={propertyClasses.propertyIcon} />
                  {t('Schedule')}
                </div>
                <div className={`${propertyClasses.propertyValue} ${classes.scheduleValue}`}>{schedule}</div>
              </div>
            </div>
          )}
          <OpportunityProperties listedProperties={listedProperties} excludeKeys={ELIGIBILITY_SECTION_PROPS} />
          {ELIGIBILITY_SECTION_PROPS.some((key) => listedProperties[key]) && (
            <>
              <div className={classes.eligibilityBadge}>{t('Eligibility requirements')}</div>
              <OpportunityProperties
                listedProperties={listedProperties}
                includeKeys={ELIGIBILITY_SECTION_PROPS}
                noLabelKeys={['elig-available-everyone']}
                hideDividers
              />
            </>
          )}
          {signupUrl && (
            <>
              <hr className={classes.externalLinkDivider} />
              <div className={classes.externalLinkRow}>
                <span className={classes.externalLinkLabel}>{t('External Link:')}</span>
                <a href={signupUrl} target="_blank" rel="noopener noreferrer" className={classes.externalLinkBadge}>
                  <FontAwesomeIcon icon={['fas', 'globe']} className={classes.externalLinkIcon} />
                  {signupUrl}
                </a>
              </div>
            </>
          )}
          {attachments.length > 0 && (
            <>
              <hr className={classes.externalLinkDivider} />
              <div className={classes.attachmentList}>
                {attachments.map((attachment, i) => (
                  <a
                    key={i}
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.attachmentLink}
                    onClick={() => handleAttachmentClick(attachment)}
                  >
                    <FontAwesomeIcon icon={['fas', 'file-arrow-down']} className={classes.attachmentIcon} />
                    {t('Document')} – {attachment.display_name}
                  </a>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailsSection;
